import { useState, useRef, useEffect } from 'react'
import { colors, fonts } from './UI'
import { useQuickSpends, useQuickIncome, useBudgets, useAccounts } from '../hooks/useData'
import { currentPayCycleStart } from '../lib/payday'
import { useAuth } from '../hooks/useAuth'
import { containsSensitiveData, SENSITIVE_DATA_ERROR } from '../lib/sensitiveDataCheck'

const SPEND_CATEGORIES = [
  'Housing','Groceries','Eating Out','Transport','Utilities',
  'Entertainment','Subscriptions','Clothing','Health','Personal Care',
  'Baby','Pet','Business','Loan','Bills','Savings','Investments','Insurance','Other',
]

const INCOME_CATEGORIES = [
  'Salary', 'Freelance', 'Benefits', 'Rental',
  'Bonus', 'Dividends', 'Side hustle', 'Sold something', 'Other',
]

function Chip({ label, selected, onClick, color }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 600,
      cursor: 'pointer', border: 'none', fontFamily: fonts.body,
      background: selected ? (color ? `${color}20` : 'rgba(201,151,43,0.18)') : 'rgba(255,255,255,0.05)',
      color: selected ? (color || '#e8b84b') : colors.muted,
      outline: selected ? `1px solid ${color ? `${color}60` : 'rgba(201,151,43,0.4)'}` : 'none',
      transition: 'all 0.15s',
    }}>{label}</button>
  )
}

export default function QuickSpendModal({ onClose, payCycleStart, defaultTab, basicMode = false }) {
  const { profile } = useAuth()
  const payDay = profile?.pay_day
  const shiftWeekends = profile?.pay_day_shift_weekends !== false
  const isDual = profile?.income_type === 'dual'
  const person1 = profile?.income_1_label || 'Me'
  const person2 = profile?.income_2_label || 'Partner'
  const cycleStart = payCycleStart || currentPayCycleStart(payDay, new Date(), shiftWeekends)

  const { addQuickSpend } = useQuickSpends(cycleStart)
  const { addQuickIncome } = useQuickIncome(cycleStart)
  const { budgets } = useBudgets(cycleStart)
  const { accounts, upsertAccount } = useAccounts()
  const includeCCinSTS = profile?.include_cc_in_sts === true
  const spendableAccounts = accounts.filter(a => ['current', 'credit_card', 'credit'].includes(a.type))
  const depositAccounts = accounts.filter(a => ['current', 'savings'].includes(a.type))

  // Show categories that exist in budget list (any amount, including zero) + always include Eating Out
  const budgetedCategories = budgets.map(b => b.category).filter(Boolean)
  const mergedSpendCategories = [...new Set([
    'Eating Out',
    ...(budgetedCategories.length > 0 ? budgetedCategories : SPEND_CATEGORIES)
  ])]

  const [tab, setTab] = useState(defaultTab === 'income' ? 'income' : 'spend')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [category, setCategory] = useState('')
  const [owner, setOwner] = useState(isDual ? person1 : 'Joint')
  const [accountId, setAccountId] = useState('')
  const [noteError, setNoteError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const amountRef = useRef(null)

  useEffect(() => {
    setTimeout(() => amountRef.current?.focus(), 80)
  }, [])

  function switchTab(t) {
    setTab(t); setAmount(''); setNote(''); setCategory(''); setAccountId(''); setSaveError(''); setNoteError('')
  }

  async function handleSubmit() {
    const n = parseFloat(amount)
    if (!Number.isFinite(n) || n <= 0) return
    if (!cycleStart) { setSaveError('Pay cycle not loaded - please try again.'); return }
    if (containsSensitiveData(note)) { setNoteError(SENSITIVE_DATA_ERROR); return }
    setNoteError(''); setSaveError('')
    setSaving(true)

    let error
    if (tab === 'spend') {
      ({ error } = await addQuickSpend({ amount: n, note: note.trim() || null, payCycleStart: cycleStart, budget_category: category || null, account_id: accountId || null }))
      // Update account balance if an account was selected
      if (!error && accountId) {
        const account = accounts.find(a => a.id === accountId)
        if (account) {
          const isCreditCard = account.type === 'credit_card' || account.type === 'credit'
          // CC spend increases balance owed, current account spend reduces balance
          await upsertAccount({ ...account, balance: isCreditCard ? (account.balance || 0) + n : (account.balance || 0) - n })
        }
      }
    } else {
      ({ error } = await addQuickIncome({ amount: n, note: note.trim() || null, category: category || null, ownerLabel: isDual ? owner : 'Joint', payCycleStart: cycleStart }))
      // Credit the chosen account balance - money received increases it
      if (!error && accountId) {
        const account = accounts.find(a => a.id === accountId)
        if (account) {
          await upsertAccount({ ...account, balance: (account.balance || 0) + n })
        }
      }
    }
    setSaving(false)
    if (error) { setSaveError('Failed to save - please try again.'); return }
    setSaved(true)
    setTimeout(() => onClose(), 1200)
  }

  const isSpend = tab === 'spend'
  const accentColor = isSpend ? colors.gold : colors.green
  const fieldLabel = (text, optional) => (
    <div style={{ fontSize: '14px', fontWeight: 600, color: colors.muted, marginBottom: '5px' }}>
      {text}{optional && <span style={{ fontWeight: 400, opacity: 0.6 }}> (optional)</span>}
    </div>
  )

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: colors.cardMid, borderRadius: '24px', padding: '8px 0 32px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', width: '100%', maxWidth: '480px' }} onClick={e => e.stopPropagation()}>

        {/* Handle */}
        <div style={{ width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', margin: '8px auto 0' }} />

        {/* Tab toggle */}
        <div style={{ display: 'flex', margin: '14px 20px 0', background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '3px', gap: '3px' }}>
          {[{ id: 'spend', label: '⚡ Log a spend' }, ...(basicMode ? [] : [{ id: 'income', label: '💰 Log income' }])].map(t => (
            <button key={t.id} onClick={() => switchTab(t.id)} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontFamily: fonts.body, fontSize: '13px', fontWeight: 700, background: tab === t.id ? (t.id === 'spend' ? 'rgba(201,151,43,0.15)' : 'rgba(61,184,122,0.15)') : 'transparent', color: tab === t.id ? (t.id === 'spend' ? colors.gold : colors.green) : colors.muted, transition: 'all 0.15s' }}>
              {t.label}
            </button>
          ))}
        </div>

        {saved ? (
          <div style={{ padding: '24px 20px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: '44px', marginBottom: '10px' }}>{isSpend ? '✅' : '💰'}</div>
            <div style={{ fontFamily: fonts.heading, fontSize: '20px', fontWeight: 700, color: accentColor, marginBottom: '6px' }}>Logged!</div>
            <div style={{ fontSize: '28px', fontFamily: fonts.heading, fontWeight: 800, color: colors.text }}>{isSpend ? '-' : '+'}£{parseFloat(amount).toFixed(2)}</div>
            {note && <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, marginTop: '6px' }}>{note}</div>}
            {category && <div style={{ fontSize: '13px', fontWeight: 600, color: accentColor, marginTop: '4px' }}>{category}</div>}
          </div>
        ) : (
          <div style={{ padding: '16px 20px 0' }}>

            {/* Amount */}
            <div style={{ marginBottom: '14px' }}>
              {fieldLabel('Amount')}
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono, fontSize: '20px' }}>£</span>
                <input ref={amountRef} type="number" inputMode="decimal" step="0.01" min="0" value={amount}
                  onChange={e => setAmount(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }}
                  placeholder="0.00"
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(255,255,255,0.1)`, borderRadius: '10px', color: colors.text, fontFamily: fonts.heading, fontSize: '32px', fontWeight: 700, padding: '14px 14px 14px 36px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Note */}
            <div style={{ marginBottom: '14px' }}>
              {fieldLabel('Note', true)}
              <input type="text" value={note} onChange={e => setNote(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }}
                placeholder={isSpend ? 'e.g. petrol, lunch, taxi...' : 'e.g. October bonus, child benefit...'}
                maxLength={80}
                style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(255,255,255,0.1)`, borderRadius: '10px', color: colors.text, fontFamily: fonts.body, fontSize: '14px', padding: '11px 14px', outline: 'none', boxSizing: 'border-box' }} />
            </div>

            {/* Category chips */}
            <div style={{ marginBottom: '14px' }}>
              {fieldLabel('Category', true)}
              <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                {(isSpend ? mergedSpendCategories : INCOME_CATEGORIES).map(c => (
                  <Chip key={c} label={c} selected={category === c} color={isSpend ? colors.gold : colors.green}
                    onClick={() => setCategory(cat => cat === c ? '' : c)} />
                ))}
              </div>
            </div>

            {/* Who (income only, dual households) */}
            {!isSpend && isDual && (
              <div style={{ marginBottom: '14px' }}>
                {fieldLabel('Who')}
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[person1, person2, 'Joint'].map(p => (
                    <Chip key={p} label={p} selected={owner === p} color={colors.green}
                      onClick={() => setOwner(p)} />
                  ))}
                </div>
              </div>
            )}

            {/* Account selector - spend only */}
            {isSpend && spendableAccounts.length > 0 && (
              <div style={{ marginBottom: '14px' }}>
                {fieldLabel('Paid from', true)}
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  {spendableAccounts.map(a => {
                    const isCR = a.type === 'credit_card' || a.type === 'credit'
                    return (
                      <button key={a.id} onClick={() => setAccountId(id => id === a.id ? '' : a.id)} style={{
                        padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 600,
                        cursor: 'pointer', border: 'none', fontFamily: fonts.body,
                        background: accountId === a.id ? (isCR ? 'rgba(224,82,82,0.18)' : 'rgba(201,151,43,0.18)') : 'rgba(255,255,255,0.05)',
                        color: accountId === a.id ? (isCR ? colors.red : colors.gold) : colors.muted,
                        outline: accountId === a.id ? `1px solid ${isCR ? 'rgba(224,82,82,0.4)' : 'rgba(201,151,43,0.4)'}` : 'none',
                      }}>
                        {isCR ? '💳' : '🏦'} {a.name}{isCR ? ' (CR)' : ''}
                      </button>
                    )
                  })}
                </div>
                {accountId && (() => {
                  const acc = accounts.find(a => a.id === accountId)
                  const isCR = acc?.type === 'credit_card' || acc?.type === 'credit'
                  return acc ? (
                    <div style={{ fontSize: '12px', color: colors.muted, marginTop: '6px' }}>
                      {isCR ? `Card balance will increase to £${((acc.balance || 0) + (parseFloat(amount) || 0)).toFixed(2)}` : `Account balance will reduce to £${((acc.balance || 0) - (parseFloat(amount) || 0)).toFixed(2)}`}
                    </div>
                  ) : null
                })()}
              </div>
            )}

            {/* Account selector - income (paid into) */}
            {!isSpend && depositAccounts.length > 0 && (
              <div style={{ marginBottom: '14px' }}>
                {fieldLabel('Paid into', true)}
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  {depositAccounts.map(a => (
                    <button key={a.id} onClick={() => setAccountId(id => id === a.id ? '' : a.id)} style={{
                      padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 600,
                      cursor: 'pointer', border: 'none', fontFamily: fonts.body,
                      background: accountId === a.id ? 'rgba(61,184,122,0.18)' : 'rgba(255,255,255,0.05)',
                      color: accountId === a.id ? colors.green : colors.muted,
                      outline: accountId === a.id ? '1px solid rgba(61,184,122,0.4)' : 'none',
                    }}>
                      🏦 {a.name}
                    </button>
                  ))}
                </div>
                {accountId && (() => {
                  const acc = accounts.find(a => a.id === accountId)
                  return acc ? (
                    <div style={{ fontSize: '12px', color: colors.muted, marginTop: '6px' }}>
                      {`Account balance will rise to £${((acc.balance || 0) + (parseFloat(amount) || 0)).toFixed(2)}`}
                    </div>
                  ) : null
                })()}
              </div>
            )}

            {/* Context hint */}
            {category && (
              <div style={{ background: `${accentColor}08`, border: `1px solid ${accentColor}20`, borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: colors.muted, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>💡</span>
                <span>{isSpend ? <>Tagged to <strong style={{ color: accentColor }}>{category}</strong> - appears against that budget category.</> : <>Logged as <strong style={{ color: accentColor }}>{category}</strong> income.</>}</span>
              </div>
            )}

            {/* Errors */}
            {noteError && <div style={{ fontSize: '13px', color: colors.red, padding: '8px 12px', background: 'rgba(224,82,82,0.1)', borderRadius: '8px', marginBottom: '10px' }}>{noteError}</div>}
            {saveError && <div style={{ fontSize: '13px', color: colors.red, padding: '8px 12px', background: 'rgba(224,82,82,0.1)', borderRadius: '8px', marginBottom: '14px' }}>{saveError}</div>}

            {/* Submit */}
            <button onClick={handleSubmit} disabled={!amount || parseFloat(amount) <= 0 || saving}
              style={{ width: '100%', padding: '15px', background: !amount || parseFloat(amount) <= 0 ? `${accentColor}50` : accentColor, border: 'none', borderRadius: '12px', fontFamily: fonts.heading, fontSize: '14px', fontWeight: 700, color: isSpend ? colors.onAccent : '#fff', cursor: !amount || parseFloat(amount) <= 0 ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}>
              {saving ? 'Saving...' : `${isSpend ? 'Confirm spend' : 'Log income'}${amount && parseFloat(amount) > 0 ? ` - £${parseFloat(amount).toFixed(2)}` : ''}`}
            </button>

            <button onClick={onClose} style={{ width: '100%', padding: '12px', marginTop: '8px', background: 'transparent', border: 'none', fontFamily: fonts.body, fontSize: '14px', fontWeight: 600, color: colors.muted, cursor: 'pointer' }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  )
}
